import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getHotDeals, createAnalysisFromListing } from '../api/listingClient';
import { formatCurrency } from './FormatUtils';
import type { HotDealItem } from '../types/listing';

// ── Criteria types & localStorage helpers ──

interface HotDealCriteria {
  minRoiPct: number | null;
  maxPurchasePrice: number | null;
  zipCodes: string[];
  minQualityScore: number | null;
}

const CRITERIA_KEY = 'hot_deal_criteria';

function loadCriteria(): HotDealCriteria {
  try {
    const raw = localStorage.getItem(CRITERIA_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore parse errors */
  }
  return { minRoiPct: null, maxPurchasePrice: null, zipCodes: [], minQualityScore: null };
}

function saveCriteria(c: HotDealCriteria): void {
  localStorage.setItem(CRITERIA_KEY, JSON.stringify(c));
}

function hasCriteria(c: HotDealCriteria): boolean {
  return (
    c.minRoiPct != null ||
    c.maxPurchasePrice != null ||
    c.zipCodes.length > 0 ||
    c.minQualityScore != null
  );
}

function criteriasSummary(c: HotDealCriteria): string {
  const parts: string[] = [];
  if (c.minRoiPct != null) parts.push(`ROI > ${c.minRoiPct}%`);
  if (c.maxPurchasePrice != null) parts.push(`Max ${formatCurrency(c.maxPurchasePrice)}`);
  if (c.zipCodes.length > 0) parts.push(`Zips: ${c.zipCodes.join(', ')}`);
  if (c.minQualityScore != null) parts.push(`Quality >= ${c.minQualityScore}`);
  return parts.join(' · ');
}

function filterDeals(deals: HotDealItem[], criteria: HotDealCriteria): HotDealItem[] {
  if (!hasCriteria(criteria)) return deals;
  return deals.filter((d) => {
    if (
      criteria.minRoiPct != null &&
      (d.estimated_roi_pct == null || d.estimated_roi_pct < criteria.minRoiPct)
    )
      return false;
    if (
      criteria.maxPurchasePrice != null &&
      (d.list_price == null || d.list_price > criteria.maxPurchasePrice)
    )
      return false;
    if (criteria.zipCodes.length > 0 && (!d.zip_code || !criteria.zipCodes.includes(d.zip_code)))
      return false;
    if (
      criteria.minQualityScore != null &&
      (d.quality_score == null || d.quality_score < criteria.minQualityScore)
    )
      return false;
    return true;
  });
}

// ── Criteria Modal ──

function HotDealCriteriaModal({
  criteria,
  onSave,
  onClose,
}: {
  criteria: HotDealCriteria;
  onSave: (c: HotDealCriteria) => void;
  onClose: () => void;
}) {
  const [minRoi, setMinRoi] = useState(
    criteria.minRoiPct != null ? String(criteria.minRoiPct) : '',
  );
  const [maxPrice, setMaxPrice] = useState(
    criteria.maxPurchasePrice != null ? String(criteria.maxPurchasePrice) : '',
  );
  const [zips, setZips] = useState(criteria.zipCodes.join(', '));
  const [minQuality, setMinQuality] = useState(
    criteria.minQualityScore != null ? String(criteria.minQualityScore) : '',
  );

  const handleSave = () => {
    const parsed: HotDealCriteria = {
      minRoiPct: minRoi.trim() ? Number(minRoi) : null,
      maxPurchasePrice: maxPrice.trim() ? Number(maxPrice.replace(/[^0-9.]/g, '')) : null,
      zipCodes: zips
        .split(',')
        .map((z) => z.trim())
        .filter(Boolean),
      minQualityScore: minQuality.trim() ? Math.min(10, Math.max(1, Number(minQuality))) : null,
    };
    saveCriteria(parsed);
    onSave(parsed);
  };

  const handleClear = () => {
    const empty: HotDealCriteria = {
      minRoiPct: null,
      maxPurchasePrice: null,
      zipCodes: [],
      minQualityScore: null,
    };
    saveCriteria(empty);
    onSave(empty);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: 'var(--card-bg, #fff)',
          borderRadius: 'var(--radius, 8px)',
          padding: '1.5rem',
          width: '100%',
          maxWidth: 420,
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <h3 style={{ margin: '0 0 1rem', fontSize: '1.125rem' }}>Hot Deal Criteria</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <label style={{ fontSize: '0.8125rem' }}>
            <span style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
              Minimum ROI %
            </span>
            <input
              type="number"
              value={minRoi}
              onChange={(e) => setMinRoi(e.target.value)}
              placeholder="e.g. 15"
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: 'var(--radius, 4px)',
                border: '1px solid var(--border)',
                fontSize: '0.875rem',
              }}
            />
          </label>

          <label style={{ fontSize: '0.8125rem' }}>
            <span style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
              Maximum Purchase Price ($)
            </span>
            <input
              type="text"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="e.g. 1500000"
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: 'var(--radius, 4px)',
                border: '1px solid var(--border)',
                fontSize: '0.875rem',
              }}
            />
          </label>

          <label style={{ fontSize: '0.8125rem' }}>
            <span style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
              Zip Codes (comma-separated)
            </span>
            <input
              type="text"
              value={zips}
              onChange={(e) => setZips(e.target.value)}
              placeholder="e.g. 94117, 94110, 94102"
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: 'var(--radius, 4px)',
                border: '1px solid var(--border)',
                fontSize: '0.875rem',
              }}
            />
          </label>

          <label style={{ fontSize: '0.8125rem' }}>
            <span style={{ fontWeight: 600, display: 'block', marginBottom: '0.25rem' }}>
              Minimum Quality Score (1-10)
            </span>
            <input
              type="number"
              min={1}
              max={10}
              value={minQuality}
              onChange={(e) => setMinQuality(e.target.value)}
              placeholder="e.g. 7"
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: 'var(--radius, 4px)',
                border: '1px solid var(--border)',
                fontSize: '0.875rem',
              }}
            />
          </label>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '1.25rem',
            justifyContent: 'flex-end',
          }}
        >
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleClear}
            style={{ fontSize: '0.8125rem' }}
          >
            Clear All
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={onClose}
            style={{ fontSize: '0.8125rem' }}
          >
            Cancel
          </button>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleSave}
            style={{ fontSize: '0.8125rem' }}
          >
            Save Criteria
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──

function recommendationLabel(rec: string | null): string {
  if (rec === 'strong_buy') return 'Strong Buy';
  if (rec === 'buy') return 'Buy';
  return rec || '';
}

function recommendationClass(rec: string | null): string {
  if (rec === 'strong_buy') return 'recommendation-strong-buy';
  if (rec === 'buy') return 'recommendation-buy';
  return '';
}

export default function HotDealsCard() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<HotDealItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [criteria, setCriteria] = useState<HotDealCriteria>(loadCriteria);
  const [showModal, setShowModal] = useState(false);

  const filteredDeals = useMemo(() => filterDeals(deals, criteria), [deals, criteria]);

  const handleQuickAnalyze = async (e: React.MouseEvent, dealId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setAnalyzingId(dealId);
    try {
      const result = await createAnalysisFromListing(dealId);
      navigate(`/analyze/${result.analysis_id}`);
    } catch {
      setAnalyzingId(null);
    }
  };

  useEffect(() => {
    getHotDeals()
      .then((res) => setDeals(res.items.slice(0, 5)))
      .catch(() => setDeals([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="hot-deals-card">
        <div className="hot-deals-header">
          <h2 className="hot-deals-title">Hot Deals</h2>
        </div>
        <div className="hot-deals-loading">Loading hot deals...</div>
      </div>
    );
  }

  return (
    <div className="hot-deals-card">
      <div className="hot-deals-header">
        <h2 className="hot-deals-title">Hot Deals</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {filteredDeals.length > 0 && (
            <span className="hot-deals-count">{filteredDeals.length} found</span>
          )}
          <button
            className="btn btn-sm btn-secondary"
            onClick={() => setShowModal(true)}
            style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
          >
            {hasCriteria(criteria) ? 'Edit Criteria' : 'Set Criteria'}
          </button>
        </div>
      </div>

      {hasCriteria(criteria) && (
        <div
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            padding: '0 0.25rem 0.5rem',
            lineHeight: 1.4,
          }}
        >
          Criteria: {criteriasSummary(criteria)}
        </div>
      )}

      {filteredDeals.length === 0 ? (
        <p className="hot-deals-empty">
          {deals.length === 0
            ? 'No hot deals found — check back soon'
            : 'No deals match your criteria — adjust filters or clear criteria'}
        </p>
      ) : (
        <div className="hot-deals-list">
          {filteredDeals.map((deal) => (
            <Link key={deal.id} to={`/listings/${deal.id}`} className="hot-deal-item">
              <div className="hot-deal-main">
                <div className="hot-deal-address">{deal.address}</div>
                <div className="hot-deal-meta">
                  {deal.city && <span>{deal.city}</span>}
                  {deal.zip_code && <span>{deal.zip_code}</span>}
                  {deal.bedrooms != null && <span>{deal.bedrooms}bd</span>}
                  {deal.bathrooms != null && <span>{deal.bathrooms}ba</span>}
                  {deal.sqft != null && <span>{Math.round(deal.sqft).toLocaleString()} sqft</span>}
                </div>
              </div>
              <div className="hot-deal-stats">
                <span className="hot-deal-price">{formatCurrency(deal.list_price)}</span>
                {deal.quality_score != null && (
                  <span className="hot-deal-quality-badge">{deal.quality_score}/10</span>
                )}
                {deal.recommendation && (
                  <span
                    className={`recommendation-badge ${recommendationClass(deal.recommendation)}`}
                  >
                    {recommendationLabel(deal.recommendation)}
                  </span>
                )}
                {deal.estimated_roi_pct != null && (
                  <span className="hot-deal-roi">{deal.estimated_roi_pct.toFixed(1)}% ROI</span>
                )}
                <button
                  className="btn-quick-analyze btn-quick-analyze-sm"
                  onClick={(e) => handleQuickAnalyze(e, deal.id)}
                  disabled={analyzingId === deal.id}
                  title="Create analysis from this listing"
                >
                  {analyzingId === deal.id ? '...' : '⚡'}
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showModal && (
        <HotDealCriteriaModal
          criteria={criteria}
          onSave={(c) => {
            setCriteria(c);
            setShowModal(false);
          }}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
